import { Close as CloseIcon } from "@mui/icons-material";
import { Box, Grid, IconButton, TextField } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { LayoutGrid } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import ImageUpload from "@/shared/components/file-upload/ImageUpload";
import {
  ExperienceErrorText,
  ExperienceFieldGuide,
  ExperienceFieldLabel,
  ExperienceHeader,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  fieldHelperText,
  getExperienceFieldSx,
  getExperienceTheme,
  isSeasonMomentVariant,
} from "./ExperienceUI";

interface ImageGridData {
  images?: string[];
  description?: string;
}

interface ImageGridExperienceProps {
  darkMode: boolean;
  data?: ImageGridData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: ImageGridData) => void;
}

const getValidationSchema = (t: any, isSeason: boolean) =>
  isSeason
    ? Yup.object({
        images: Yup.array()
          .of(Yup.string().required())
          .min(1, t("image_grid_min_one_season"))
          .required(),
        description: Yup.string().nullable(),
      })
    : Yup.object({
    images: Yup.array()
      .of(Yup.string().required())
      .min(1, t("at_least_one_image_is_required"))
      .required(t("images_are_required")),
    description: Yup.string()
      .required(t("description_is_required"))
      .min(10, t("description_must_be_at_least_10_characters")),
  });

const ImageGridExperience: React.FC<ImageGridExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const theme = getExperienceTheme(darkMode);
  const validationSchema = getValidationSchema(t, isSeason);
  const isInternalUpdateRef = useRef(false);
  const fieldSx = getExperienceFieldSx(darkMode);
  const dummyImages = [t("dummy_image_grid_1"), t("dummy_image_grid_2")];

  const formik = useFormik<ImageGridData>({
    initialValues: {
      images: data?.images ?? (isSeason ? [] : dummyImages),
      description:
        data?.description ?? (isSeason ? "" : t("dummy_image_grid_description")),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: !isSeason,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const newValues: ImageGridData = {
        images: data.images ?? (isSeason ? [] : dummyImages),
        description:
          data.description ?? (isSeason ? "" : t("dummy_image_grid_description")),
      };
      const hasChanged = Object.keys(newValues).some((key) => {
        if (key === "images") {
          return (
            JSON.stringify(formik.values.images) !==
            JSON.stringify(newValues.images)
          );
        }
        return (
          formik.values[key as keyof ImageGridData] !==
          newValues[key as keyof ImageGridData]
        );
      });
      if (hasChanged) {
        formik.setValues(newValues);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (onDataChange) {
      const timeoutId = setTimeout(() => {
        isInternalUpdateRef.current = true;
        onDataChange({
          images: formik.values.images,
          description: formik.values.description,
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const remainingSlots = 8 - (formik.values.images?.length || 0);
      const filesToProcess = fileArray.slice(0, remainingSlots);
      const newImages: string[] = [];
      let loadedCount = 0;

      filesToProcess.forEach((file, index) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          newImages[index] = result;
          loadedCount++;

          if (loadedCount === filesToProcess.length) {
            const updatedImages = [
              ...(formik.values.images || []),
              ...newImages,
            ];
            formik.setFieldValue("images", updatedImages);
            formik.setFieldError("images", undefined);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveImage = (index: number) => {
    const updatedImages = (formik.values.images || []).filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("images", updatedImages);
    formik.setFieldTouched("images", true);
  };

  const getGridColumns = () => {
    const imageCount = formik.values.images?.length || 0;
    if (imageCount <= 1) return 12;
    if (imageCount === 2) return 6;
    if (imageCount === 3) return 4;
    return 3;
  };

  const gridColumns = getGridColumns();

  return (
    <FormikProvider value={formik}>
      <ExperienceShell gap={2.5}>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={LayoutGrid}
            title={t("image_grid")}
            subtitle={t("image_grid_header_subtitle")}
          />
        )}

        <ExperienceFieldGuide darkMode={darkMode}>
          {isSeason
            ? t("image_grid_field_guide_season")
            : t("image_grid_field_guide")}
        </ExperienceFieldGuide>

        <ExperienceFieldLabel darkMode={darkMode}>
          {isSeason
            ? t("image_grid_photos_label_season")
            : t("image_grid_gallery_label", {
                count: (formik.values.images || []).length,
              })}
        </ExperienceFieldLabel>

        {(formik.values.images || []).length > 0 ? (
          <Grid container spacing={2}>
            {formik.values.images?.map((image, index) => (
              <Grid item xs={12} sm={gridColumns} key={index}>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1",
                    borderRadius: "14px",
                    overflow: "hidden",
                    bgcolor: theme.surfaceElevated,
                    border: `1px solid ${theme.border}`,
                    transition: "transform 0.2s ease",
                    "&:hover": { transform: "scale(1.02)" },
                  }}
                >
                  <img
                    src={image}
                    alt={t("image_grid_alt", { index: index + 1 })}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    onClick={() => handleRemoveImage(index)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "rgba(0, 0, 0, 0.65)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.85)" },
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              width: "100%",
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "16px",
              border: `2px dashed ${
                formik.touched.images && formik.errors.images
                  ? darkMode
                    ? "var(--destructive-500)"
                    : "var(--destructive-600)"
                  : theme.uploadBorder
              }`,
              bgcolor: theme.uploadBg,
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: theme.borderHover },
            }}
          >
            <ImageUpload
              onFileSelect={handleFileSelect}
              accept="image/*"
              multiple={true}
              maxFiles={8}
              layout="column"
            />
          </Box>
        )}
        {formik.touched.images && formik.errors.images && (
          <ExperienceErrorText darkMode={darkMode} sx={{ mt: -1 }}>
            {formik.errors.images as string}
          </ExperienceErrorText>
        )}

        {(formik.values.images || []).length > 0 &&
          (formik.values.images || []).length < 8 && (
            <ImageUpload
              onFileSelect={handleFileSelect}
              accept="image/*"
              multiple={true}
              maxFiles={8 - (formik.values.images?.length || 0)}
              layout="row"
            />
          )}

        <TextField
          label={
            <>
              {isSeason
                ? t("image_grid_caption_label_season")
                : t("image_grid_caption_label")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="description"
          value={formik.values.description}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldError("description", undefined);
          }}
          onBlur={formik.handleBlur}
          fullWidth
          multiline
          minRows={4}
          placeholder={t("add_description_for_image_grid")}
          error={Boolean(
            formik.touched.description && formik.errors.description
          )}
          helperText={fieldHelperText(
            formik.touched.description && formik.errors.description
              ? String(formik.errors.description)
              : undefined,
            t("image_grid_description_helper")
          )}
          sx={fieldSx}
        />
      </ExperienceShell>
    </FormikProvider>
  );
};

export default ImageGridExperience;
